import { supabase } from './supabaseClient';
import { Categoria, FaseDeObra } from './types';

// Fases da Obra
export const getFasesDeObra = async (includeInactive = false) => {
    let query = supabase.from('fases_da_obra').select('*').order('tipo_fluxo').order('ordem').order('nome');
    if (!includeInactive) {
        query = query.eq('ativo', true);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data;
};

export const addFaseDeObra = async (fase: Omit<FaseDeObra, 'id'>) => {
    const { data, error } = await supabase.from('fases_da_obra').insert([fase]).select();
    if (error) throw error;
    return data;
};

export const updateFaseDeObra = async (id: number, fase: Partial<FaseDeObra>) => {
    const { data, error } = await supabase.from('fases_da_obra').update(fase).eq('id', id).select();
    if (error) throw error;
    return data;
};

export const swapFasesOrdem = async (faseA: { id: number; ordem: number }, faseB: { id: number; ordem: number }) => {
    const { error: err1 } = await supabase.from('fases_da_obra').update({ ordem: faseB.ordem }).eq('id', faseA.id);
    if (err1) throw err1;
    const { error: err2 } = await supabase.from('fases_da_obra').update({ ordem: faseA.ordem }).eq('id', faseB.id);
    if (err2) throw err2;
};

export const deleteFaseDeObra = async (id: number) => {
    const { error } = await supabase.from('fases_da_obra').update({ ativo: false }).eq('id', id);
    if (error) throw error;
};

export const reactivateFaseDeObra = async (id: number) => {
    const { error } = await supabase.from('fases_da_obra').update({ ativo: true }).eq('id', id);
    if (error) throw error;
};

// Categorias
export const getCategorias = async (includeInactive = false): Promise<Categoria[]> => {
    let query = supabase.from('categorias').select('*').order('nome');
    if (!includeInactive) {
        query = query.eq('ativo', true);
    }
    const { data, error } = await query;
    if (error) throw error;
    const categorias = data || [];
    const map: { [key: number]: Categoria } = {};
    const roots: Categoria[] = [];
    categorias.forEach(cat => { map[cat.id] = { ...cat, subcategorias: [] }; });
    categorias.forEach(cat => {
        if (cat.categoria_pai_id && map[cat.categoria_pai_id]) {
            map[cat.categoria_pai_id].subcategorias?.push(map[cat.id]);
        } else {
            roots.push(map[cat.id]);
        }
    });
    return roots;
};

export const getCategoriasFlat = async (includeInactive = false): Promise<Categoria[]> => {
    let query = supabase.from('categorias').select('*').order('nome');
    if (!includeInactive) {
        query = query.eq('ativo', true);
    }
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
};

export const addCategoria = async (categoria: Omit<Categoria, 'id' | 'subcategorias' | 'ativo'> & { ativo?: boolean }) => {
    const { data, error } = await supabase.from('categorias').insert([categoria]).select();
    if (error) throw error;
    return data;
};

export const updateCategoria = async (id: number, categoria: Partial<Omit<Categoria, 'id' | 'subcategorias'>>) => {
    const { data, error } = await supabase.from('categorias').update(categoria).eq('id', id).select();
    if (error) throw error;
    return data;
};

export const deleteCategoria = async (id: number) => {
    const { error } = await supabase.from('categorias').update({ ativo: false }).eq('id', id);
    if (error) throw error;
};

export const reactivateCategoria = async (id: number) => {
    const { error } = await supabase.from('categorias').update({ ativo: true }).eq('id', id);
    if (error) throw error;
};

// Itens do Catálogo
export const getItens = async (filters: { fase_obra_id?: number, includeInactive?: boolean } = {}) => {
    const selectStr = filters.fase_obra_id
        ? `*, categorias (nome, categoria_pai_id), catalogo_itens_fases!inner ( fase_obra_id, fases_da_obra (id, nome, tipo_fluxo, ordem) )`
        : `*, categorias (nome, categoria_pai_id), catalogo_itens_fases ( fase_obra_id, fases_da_obra (id, nome, tipo_fluxo, ordem) )`;

    let query = supabase.from('catalogo_itens').select(selectStr).order('descricao');
    if (!filters.includeInactive) {
        query = query.eq('ativo', true);
    }
    if (filters.fase_obra_id) {
        query = query.eq('catalogo_itens_fases.fase_obra_id', filters.fase_obra_id);
    }
    const { data, error } = await query;
    if (error) throw error;
    return (data || []).map((item: any) => ({
        ...item,
        categoria_nome: item.categorias?.nome || 'Sem categoria',
        fases: (item.catalogo_itens_fases || [])
            .map((j: any) => j.fases_da_obra)
            .filter(Boolean)
            .sort((a: any, b: any) => (a.tipo_fluxo || '').localeCompare(b.tipo_fluxo || '') || (a.ordem || 0) - (b.ordem || 0))
    }));
};

export const addItem = async (item: any, fases: number[]) => {
    const { data, error } = await supabase.rpc('upsert_catalogo_item', {
        p_id: null,
        p_descricao: item.descricao,
        p_valor_unitario_padrao: Number(item.valor_unitario_padrao) || 0,
        p_categoria_id: Number(item.categoria_id),
        p_fases: fases
    });
    if (error) throw error;
    return data;
};

export const addMultipleItens = async (itensComFases: { item: any, fases: number[] }[]) => {
    const { data, error } = await supabase.rpc('import_catalogo_itens_batch', {
        p_items: itensComFases
    });
    if (error) throw error;
    return data;
};

export const updateItem = async (id: number, item: any, fases: number[]) => {
    const { data, error } = await supabase.rpc('upsert_catalogo_item', {
        p_id: id,
        p_descricao: item.descricao,
        p_valor_unitario_padrao: Number(item.valor_unitario_padrao) || 0,
        p_categoria_id: Number(item.categoria_id),
        p_fases: fases
    });
    if (error) throw error;
    return data;
};

export const deleteItem = async (id: number) => {
    const { error } = await supabase.from('catalogo_itens').update({ ativo: false }).eq('id', id);
    if (error) throw error;
};

export const reactivateItem = async (id: number) => {
    const { error } = await supabase.from('catalogo_itens').update({ ativo: true }).eq('id', id);
    if (error) throw error;
};

export const updateMultipleItemPrices = async (updates: { id: number; valor_unitario_padrao: number }[]) => {
    await Promise.all(
        updates.map(async ({ id, valor_unitario_padrao }) => {
            const { error } = await supabase
                .from('catalogo_itens')
                .update({ valor_unitario_padrao })
                .eq('id', id);
            if (error) throw error;
        })
    );
};

